import React, { useState, useRef, useEffect } from 'react';
import api from '../api/axios';

const SectionDesignEditor = ({ page, settings, onUpdate }) => {
    const [elements, setElements] = useState(settings.elements || []);
    const [selectedElementId, setSelectedElementId] = useState(null);
    const canvasRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [clientUrl, setClientUrl] = useState(process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000');
    const [availableGroups, setAvailableGroups] = useState([]);
    const [availableSubgroups, setAvailableSubgroups] = useState([]);
    const [stations, setStations] = useState([]);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Sync state with parent
    useEffect(() => {
        onUpdate('elements', elements);
    }, [elements]);

    useEffect(() => {
        const fetchStations = async () => {
            try {
                const res = await api.get('/stations');
                setStations(res.data || []);
            } catch (err) {
                console.error("Error fetching stations in SectionDesignEditor", err);
            }
        };
        fetchStations();

        if (page === 'agenda') {
            const fetchGroups = async () => {
                try {
                    const res = await api.get('/agenda');
                    const groups = Array.from(new Set(res.data.map(e => e.group_name).filter(Boolean)));
                    const subgroups = Array.from(new Set(res.data.map(e => e.subgroup_name).filter(Boolean)));
                    setAvailableGroups(groups);
                    setAvailableSubgroups(subgroups);
                } catch (err) {
                    console.error("Error fetching agenda groups", err);
                }
            };
            fetchGroups();
        }
    }, [page]);

    const handleAddElement = (type = 'image') => {
        const newElement = {
            id: Date.now().toString(),
            name: type === 'text' ? 'Texto Nuevo' : `Elemento ${elements.length + 1}`,
            type: type,
            url: '',
            text: type === 'text' ? 'Escribe aquí tu texto...' : '',
            color: '#ffffff',
            fontSize: '24px',
            fontFamily: 'Alliance-Light',
            fontWeight: 'bold',
            x: 50,
            y: 50,
            scale: 1,
            animation: 'none',
            effect: 'none',
            zIndex: elements.length + 1
        };
        setElements([...elements, newElement]);
        setSelectedElementId(newElement.id);
    };

    const handleRemoveElement = (id) => {
        // If removing the special elements, toggle off titleFreePosition
        if (id === 'section-title' || id === 'section-subtitle') {
            onUpdate('titleFreePosition', false);
        }
        setElements(elements.filter(el => el.id !== id));
        if (selectedElementId === id) setSelectedElementId(null);
    };

    const updateElement = (id, key, value) => {
        setElements(elements.map(el => el.id === id ? { ...el, [key]: value } : el));
    };

    const handleMouseDown = (e, id) => {
        e.stopPropagation();
        setSelectedElementId(id);
        setIsDragging(true);

        const el = elements.find(el => el.id === id);
        if (!el || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
        const mouseY = ((e.clientY - rect.top) / rect.height) * 100;

        setDragOffset({
            x: mouseX - el.x,
            y: mouseY - el.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !selectedElementId || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        let newX = ((e.clientX - rect.left) / rect.width) * 100 - dragOffset.x;
        let newY = ((e.clientY - rect.top) / rect.height) * 100 - dragOffset.y;

        // Constraint to bounds (0 to 100)
        newX = Math.max(0, Math.min(100, newX));
        newY = Math.max(0, Math.min(100, newY));

        updateElement(selectedElementId, 'x', newX);
        updateElement(selectedElementId, 'y', newY);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const selectedElement = elements.find(el => el.id === selectedElementId);

    const getEffectStyle = (effect) => {
        switch(effect) {
            case 'neon': return 'drop-shadow(0 0 10px rgba(157, 78, 221, 0.8)) drop-shadow(0 0 20px rgba(157, 78, 221, 0.4))';
            case 'shadow': return 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.3))';
            case 'glow': return 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.8))';
            default: return 'none';
        }
    };

    if (isFullScreen) {
        return (
            <div className="fixed inset-0 z-[300] bg-slate-950/98 backdrop-blur-xl p-8 flex flex-col overflow-hidden animate-fadeIn text-slate-200">
                {/* Full Screen Header */}
                <header className="flex justify-between items-center mb-6 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-4">
                        <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">
                            Editor Visual Pantalla Completa — <span className="text-purple-400">{page}</span>
                        </h2>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            type="button"
                            onClick={() => setIsFullScreen(false)}
                            className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg shadow-red-900/30"
                        >
                            ✕ Salir de Pantalla Completa
                        </button>
                    </div>
                </header>

                <div className="flex-grow grid grid-cols-1 lg:grid-cols-12 gap-8 overflow-hidden">
                    {/* Controls Panel (Left) */}
                    <div className="lg:col-span-3 space-y-6 overflow-y-auto pr-2 custom-scrollbar pb-10">
                        <div className="bg-slate-900/80 border border-white/5 p-6 rounded-[30px] space-y-6">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Imagen de Fondo</label>
                                <input 
                                    className="w-full bg-white/5 border border-white/10 p-4 rounded-2xl text-xs font-mono text-white" 
                                    placeholder="URL PNG/JPG" 
                                    value={settings.bgImage || ''} 
                                    onChange={e => onUpdate('bgImage', e.target.value)} 
                                />
                            </div>

                            {(page === 'concentrese' || page === 'laberinto' || page === 'trivia') && (
                                <div className="p-4 bg-emerald-950/40 border border-emerald-900/50 rounded-2xl space-y-3">
                                    <label className="block text-[10px] font-black text-emerald-300 uppercase tracking-widest">Estación de Puntos Asignada</label>
                                    <p className="text-[8px] text-emerald-400 font-medium">Selecciona la estación donde se acumularán los puntos al completar la actividad</p>
                                    <select
                                        className="w-full bg-slate-900 border border-emerald-800/40 p-3 rounded-xl text-xs text-white"
                                        value={settings.stationId || ''}
                                        onChange={e => onUpdate('stationId', e.target.value)}
                                    >
                                        <option value="">Sin Estación Asignada</option>
                                        {stations.map(st => (
                                            <option key={st.id} value={st.id}>
                                                {st.name} ({st.code})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {page === 'agenda' && (
                                <div className="p-4 bg-purple-950/40 border border-purple-900/50 rounded-2xl space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <label className="text-[10px] font-black text-purple-300 uppercase tracking-widest">Agenda como Imagen Única</label>
                                            <p className="text-[8px] text-purple-400 mt-0.5">Mostrar la agenda en pantalla completa como imagen sin header ni footer</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="sr-only peer" 
                                                checked={settings.agendaImageOnly || false} 
                                                onChange={e => onUpdate('agendaImageOnly', e.target.checked)} 
                                            />
                                            <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-500"></div>
                                        </label>
                                    </div>

                                    {settings.agendaImageOnly && (
                                        <div className="space-y-3 pt-2 border-t border-purple-900/40">
                                            <div>
                                                <label className="block text-[9px] font-black text-purple-300 uppercase tracking-widest mb-1">URL Imagen Agenda Desktop (PC)</label>
                                                <input 
                                                    className="w-full bg-black/40 border border-purple-800/40 p-3 rounded-xl text-xs text-white placeholder-gray-500"
                                                    placeholder="https://ejemplo.com/agenda_pc.png"
                                                    value={settings.agendaImageDesktop || ''}
                                                    onChange={e => onUpdate('agendaImageDesktop', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-purple-300 uppercase tracking-widest mb-1">URL Imagen Agenda Móvil (Celular)</label>
                                                <input 
                                                    className="w-full bg-black/40 border border-purple-800/40 p-3 rounded-xl text-xs text-white placeholder-gray-500"
                                                    placeholder="https://ejemplo.com/agenda_mobile.png"
                                                    value={settings.agendaImageMobile || ''}
                                                    onChange={e => onUpdate('agendaImageMobile', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Texto Legal / Footer</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" checked={settings.showLegalText || false} onChange={e => onUpdate('showLegalText', e.target.checked)} />
                                        <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                                    </label>
                                </div>
                                {settings.showLegalText && (
                                    <input 
                                        className="w-full bg-white/5 border border-white/10 p-4 rounded-2xl text-[10px] font-medium text-white"
                                        placeholder="URL del texto legal"
                                        value={settings.legalTextUrl || ''}
                                        onChange={e => onUpdate('legalTextUrl', e.target.value)}
                                    />
                                )}
                            </div>


                            <div className="pt-6 border-t border-white/5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Ubicación Libre del Título</label>
                                        <p className="text-[8px] text-slate-500 mt-1 font-medium leading-none">Permite arrastrar el título y subtítulo libremente en el canvas</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="sr-only peer" 
                                            checked={settings.titleFreePosition || false} 
                                            onChange={e => {
                                                const val = e.target.checked;
                                                onUpdate('titleFreePosition', val);
                                                
                                                if (val) {
                                                    const newElements = [...elements];
                                                    if (!newElements.some(el => el.id === 'section-title')) {
                                                        newElements.push({
                                                            id: 'section-title',
                                                            name: '👑 Título Principal',
                                                            type: 'title-text',
                                                            x: 50,
                                                            y: 15,
                                                            scale: 1.3,
                                                            animation: 'none',
                                                            effect: 'none',
                                                            zIndex: 50
                                                        });
                                                    }
                                                    if (!newElements.some(el => el.id === 'section-subtitle')) {
                                                        newElements.push({
                                                            id: 'section-subtitle',
                                                            name: '✨ Subtítulo Principal',
                                                            type: 'subtitle-text',
                                                            x: 50,
                                                            y: 22,
                                                            scale: 1.0,
                                                            animation: 'none',
                                                            effect: 'none',
                                                            zIndex: 49
                                                        });
                                                    }
                                                    setElements(newElements);
                                                } else {
                                                    setElements(elements.filter(el => el.id !== 'section-title' && el.id !== 'section-subtitle'));
                                                }
                                            }} 
                                        />
                                        <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                                    </label>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        type="button"
                                        onClick={() => handleAddElement('image')}
                                        className="bg-indigo-950/40 text-indigo-400 border border-indigo-900/30 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-900/40 transition-all"
                                    >
                                        + Imagen Libre
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => handleAddElement('text')}
                                        className="bg-purple-950/40 text-purple-400 border border-purple-900/30 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-purple-900/40 transition-all"
                                    >
                                        + Texto Libre
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
                            {elements.map(el => (
                                <div 
                                    key={el.id}
                                    onClick={() => setSelectedElementId(el.id)}
                                    className={`p-3 rounded-xl cursor-pointer border flex justify-between items-center transition-all ${selectedElementId === el.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg' : 'bg-slate-900 border-white/5 text-slate-400 hover:border-indigo-900'}`}
                                >
                                    <span className="text-xs font-bold truncate">{el.name}</span>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleRemoveElement(el.id); }}
                                        className={`text-xs ${selectedElementId === el.id ? 'text-white/70 hover:text-white' : 'text-slate-500 hover:text-red-500'}`}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Canvas & Properties Panel (Right) */}
                    <div className="lg:col-span-9 flex flex-col md:flex-row gap-8 overflow-hidden h-[78vh]">
                        {/* Canvas en el centro */}
                        <div className="flex-grow bg-slate-900/30 rounded-[40px] border border-white/5 p-8 flex items-center justify-center relative shadow-inner overflow-hidden">
                            <div 
                                className="relative bg-slate-800 rounded-[30px] border-4 border-white/10 shadow-2xl overflow-hidden aspect-video select-none w-full max-w-5xl"
                                ref={canvasRef}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                            >
                                {/* Background */}
                                {settings.bgImage && (
                                    <img src={settings.bgImage} alt="Background" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                                )}
                                
                                {/* Elements */}
                                {elements.map(el => (
                                    <div
                                        key={el.id}
                                        onMouseDown={(e) => handleMouseDown(e, el.id)}
                                        className={`absolute cursor-move transition-transform ${selectedElementId === el.id ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}`}
                                        style={{
                                            left: `${el.x}%`,
                                            top: `${el.y}%`,
                                            transform: `translate(-50%, -50%) scale(${el.scale || 1})`,
                                            zIndex: el.zIndex || 10,
                                            filter: getEffectStyle(el.effect)
                                        }}
                                    >
                                        {el.type === 'title-text' ? (
                                            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-wider drop-shadow-lg whitespace-nowrap select-none">
                                                {settings.title || 'TÍTULO SECCIÓN'}
                                            </h2>
                                        ) : el.type === 'subtitle-text' ? (
                                            settings.subtitleType === 'image' && settings.subtitleImage ? (
                                                <img src={settings.subtitleImage} alt="Subtitle logo" className="max-w-[180px] h-auto object-contain pointer-events-none" />
                                            ) : (
                                                <div className="inline-block px-4 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 select-none">
                                                    <p className="text-xs font-bold text-white uppercase tracking-widest whitespace-nowrap">
                                                        {settings.subtitle || 'Subtítulo Sección'}
                                                    </p>
                                                </div>
                                            )
                                        ) : el.type === 'text' ? (
                                            <div 
                                                style={{ 
                                                    color: el.color || '#ffffff', 
                                                    fontSize: el.fontSize || '16px',
                                                    fontFamily: el.fontFamily || 'inherit',
                                                    fontWeight: el.fontWeight || 'bold'
                                                }}
                                                className="drop-shadow-md text-center max-w-[250px] break-words select-none"
                                            >
                                                {el.text || 'Texto libre'}
                                            </div>
                                        ) : el.url ? (
                                            <img src={el.url} alt={el.name} className="max-w-[180px] max-h-[180px] object-contain pointer-events-none" />
                                        ) : (
                                            <div className="w-24 h-24 bg-slate-700/80 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-600 text-[10px] font-bold text-slate-400 p-2 text-center pointer-events-none backdrop-blur-sm">
                                                Sin Imagen
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Panel de propiedades del elemento seleccionado a la derecha */}
                        {selectedElement && (
                            <div className="w-full md:w-[320px] bg-slate-900/90 border border-white/10 p-6 rounded-[30px] overflow-y-auto pr-2 custom-scrollbar space-y-4">
                                <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-white/10 pb-2 flex justify-between items-center">
                                    <span>Propiedades</span>
                                    <span className="text-[9px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded font-bold">{selectedElement.type.toUpperCase()}</span>
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Nombre</label>
                                        <input 
                                            className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white"
                                            value={selectedElement.name}
                                            onChange={(e) => updateElement(selectedElement.id, 'name', e.target.value)}
                                        />
                                    </div>

                                    {(selectedElement.type === 'text' || selectedElement.type === 'title-text' || selectedElement.type === 'subtitle-text') ? (
                                        <>
                                            {selectedElement.type === 'text' && (
                                                <div>
                                                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Texto del Elemento</label>
                                                    <textarea 
                                                        className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white focus:outline-none min-h-[60px]"
                                                        value={selectedElement.text || ''}
                                                        onChange={(e) => updateElement(selectedElement.id, 'text', e.target.value)}
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Color de Texto</label>
                                                <div className="flex gap-2">
                                                    <input 
                                                        type="color" 
                                                        className="w-8 h-8 rounded-lg cursor-pointer border-none p-0 bg-transparent" 
                                                        value={selectedElement.color || '#ffffff'} 
                                                        onChange={(e) => updateElement(selectedElement.id, 'color', e.target.value)} 
                                                    />
                                                    <input 
                                                        className="w-full bg-white/5 border border-white/10 p-2 rounded-lg text-xs font-mono uppercase text-white" 
                                                        value={selectedElement.color || '#ffffff'} 
                                                        onChange={(e) => updateElement(selectedElement.id, 'color', e.target.value)} 
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Tipografía</label>
                                                <select 
                                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white appearance-none"
                                                    value={selectedElement.fontFamily || 'Alliance-Light'}
                                                    onChange={(e) => updateElement(selectedElement.id, 'fontFamily', e.target.value)}
                                                >
                                                    <option value="Alliance-Light">Alliance Light</option>
                                                    <option value="Alliance-SemiBold">Alliance SemiBold</option>
                                                    <option value="Drina">Drina (Script)</option>
                                                    <option value="sans-serif">Sans Serif</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Tamaño Texto</label>
                                                <input 
                                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white"
                                                    placeholder="24px o 1.5rem"
                                                    value={selectedElement.fontSize || '24px'}
                                                    onChange={(e) => updateElement(selectedElement.id, 'fontSize', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Grosor de Texto</label>
                                                <select 
                                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white appearance-none"
                                                    value={selectedElement.fontWeight || 'bold'}
                                                    onChange={(e) => updateElement(selectedElement.id, 'fontWeight', e.target.value)}
                                                >
                                                    <option value="normal">Normal</option>
                                                    <option value="bold">Bold (Negrita)</option>
                                                    <option value="black">Black (Gruesa)</option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <div>
                                            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">URL Imagen</label>
                                            <input 
                                                className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs font-mono text-white"
                                                placeholder="URL PNG/SVG"
                                                value={selectedElement.url || ''}
                                                onChange={(e) => updateElement(selectedElement.id, 'url', e.target.value)}
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Escala ({selectedElement.scale || 1}x)</label>
                                        <input 
                                            type="range"
                                            min="0.1" max="3" step="0.1"
                                            className="w-full accent-indigo-600"
                                            value={selectedElement.scale || 1}
                                            onChange={(e) => updateElement(selectedElement.id, 'scale', parseFloat(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Efecto</label>
                                        <select 
                                            className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white appearance-none"
                                            value={selectedElement.effect || 'none'}
                                            onChange={(e) => updateElement(selectedElement.id, 'effect', e.target.value)}
                                        >
                                            <option value="none">Sin Efecto</option>
                                            <option value="shadow">Sombreado Básico</option>
                                            <option value="neon">Neón Difusión</option>
                                            <option value="glow">Glow Blanco</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Animación de Entrada</label>
                                        <select 
                                            className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-xs text-white appearance-none"
                                            value={selectedElement.animation || 'none'}
                                            onChange={(e) => updateElement(selectedElement.id, 'animation', e.target.value)}
                                        >
                                            <option value="none">Sin Animación</option>
                                            <option value="fade-in">Fade In</option>
                                            <option value="slide-left">Slide Izquierda</option>
                                            <option value="slide-right">Slide Derecha</option>
                                            <option value="bounce">Bounce</option>
                                        </select>
                                    </div>
                                    <div className="pt-4">
                                        <button 
                                            type="button"
                                            onClick={() => handleRemoveElement(selectedElement.id)}
                                            className="w-full bg-red-600/10 hover:bg-red-600/20 text-red-400 border border-red-500/20 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all"
                                        >
                                            Eliminar Elemento
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-50 rounded-[40px] border border-slate-100 overflow-hidden group transition-all hover:shadow-2xl hover:bg-white mb-8">
            {/* Page Header */}
            <div className="px-10 py-6 bg-white/50 border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: settings.enabled ? '#10b981' : '#94a3b8' }}></span>
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter italic">{page}</h3>
                </div>
                <div className="flex gap-3">
                    <button 
                        type="button"
                        onClick={() => setIsFullScreen(true)}
                        className="bg-indigo-600 text-white px-5 py-2.5 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-500 hover:scale-105 active:scale-95 transition-all shadow-md shadow-indigo-900/10 flex items-center gap-1.5"
                    >
                        <span>🖥️</span> Pantalla Completa
                    </button>
                    <button 
                        onClick={() => onUpdate('enabled', !settings.enabled)}
                        className={`px-6 py-2.5 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${settings.enabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-slate-200 text-slate-500'}`}
                    >
                        {settings.enabled ? 'Sección Activa' : 'Sección Oculta'}
                    </button>
                </div>
            </div>

            <div className="p-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* General Settings */}
                <div className="lg:col-span-3 space-y-6">
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fondo de Sección Desktop</label>
                        <input 
                            className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                            placeholder="URL del fondo desktop"
                            value={settings.bgImage || ''}
                            onChange={e => onUpdate('bgImage', e.target.value)}
                        />
                        {settings.bgImage && (
                            <div className="w-full h-16 rounded-2xl bg-slate-200 overflow-hidden border border-slate-100 shadow-sm mt-2">
                                <img src={settings.bgImage} className="w-full h-full object-cover" alt="Desktop Background Preview" />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fondo de Sección Móvil</label>
                        <input 
                            className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                            placeholder="URL del fondo móvil (opcional)"
                            value={settings.bgImageMobile || ''}
                            onChange={e => onUpdate('bgImageMobile', e.target.value)}
                        />
                        {settings.bgImageMobile && (
                            <div className="w-full h-16 rounded-2xl bg-slate-200 overflow-hidden border border-slate-100 shadow-sm mt-2">
                                <img src={settings.bgImageMobile} className="w-full h-full object-cover" alt="Mobile Background Preview" />
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Color Fondo</label>
                            <div className="flex items-center gap-2">
                                <input type="color" className="w-8 h-8 rounded-lg cursor-pointer border-none" value={settings.backgroundColor || ''} onChange={e => onUpdate('backgroundColor', e.target.value)} />
                                <button className="text-[9px] text-slate-400 font-bold" onClick={() => onUpdate('backgroundColor', '')}>Reset</button>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Color Texto</label>
                            <div className="flex items-center gap-2">
                                <input type="color" className="w-8 h-8 rounded-lg cursor-pointer border-none" value={settings.globalFontColor || ''} onChange={e => onUpdate('globalFontColor', e.target.value)} />
                                <button className="text-[9px] text-slate-400 font-bold" onClick={() => onUpdate('globalFontColor', '')}>Reset</button>
                            </div>
                        </div>
                    </div>

                    {(page === 'concentrese' || page === 'laberinto' || page === 'trivia') && (
                        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-2">
                            <label className="block text-[10px] font-black text-emerald-800 uppercase tracking-widest">Estación de Puntos Asignada</label>
                            <p className="text-[9px] text-emerald-600 font-medium">Asigna la estación que otorgará los puntos al ganar la actividad</p>
                            <select
                                className="w-full bg-white border border-emerald-300 p-3 rounded-xl text-xs font-bold text-slate-800"
                                value={settings.stationId || ''}
                                onChange={e => onUpdate('stationId', e.target.value)}
                            >
                                <option value="">Sin Estación Asignada</option>
                                {stations.map(st => (
                                    <option key={st.id} value={st.id}>
                                        {st.name} ({st.code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Título de la Sección</label>
                        <input 
                            className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                            placeholder="Ej: AGENDA"
                            value={settings.title || ''}
                            onChange={e => onUpdate('title', e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Tipo de Subtítulo</label>
                        <select 
                            className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm appearance-none"
                            value={settings.subtitleType || 'text'}
                            onChange={e => onUpdate('subtitleType', e.target.value)}
                        >
                            <option value="text">Texto</option>
                            <option value="image">Imagen (Logo)</option>
                        </select>
                    </div>

                    {settings.subtitleType === 'image' ? (
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">URL Imagen del Subtítulo</label>
                            <input 
                                className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                                placeholder="URL .png / .svg"
                                value={settings.subtitleImage || ''}
                                onChange={e => onUpdate('subtitleImage', e.target.value)}
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Subtítulo de la Sección</label>
                            <input 
                                className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                                placeholder="Ej: Evento Especial"
                                value={settings.subtitle || ''}
                                onChange={e => onUpdate('subtitle', e.target.value)}
                            />
                        </div>
                    )}
                    <div className="pt-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Texto Legal / Footer</label>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={settings.showLegalText || false} onChange={e => onUpdate('showLegalText', e.target.checked)} />
                                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                            </label>
                        </div>
                        {settings.showLegalText && (
                            <input 
                                className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                                placeholder="URL del texto legal (ej. https://.../legal.pdf)"
                                value={settings.legalTextUrl || ''}
                                onChange={e => onUpdate('legalTextUrl', e.target.value)}
                            />
                        )}
                    </div>
                    
                    <div className="pt-6 border-t border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Ubicación Libre del Título</label>
                                <p className="text-[8px] text-slate-400 mt-1 font-medium leading-none">Permite arrastrar el título y subtítulo libremente en el canvas</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    className="sr-only peer" 
                                    checked={settings.titleFreePosition || false} 
                                    onChange={e => {
                                        const val = e.target.checked;
                                        onUpdate('titleFreePosition', val);
                                        
                                        // Auto-create or remove special title/subtitle elements in array
                                        if (val) {
                                            const newElements = [...elements];
                                            if (!newElements.some(el => el.id === 'section-title')) {
                                                newElements.push({
                                                    id: 'section-title',
                                                    name: '👑 Título Principal',
                                                    type: 'title-text',
                                                    x: 50,
                                                    y: 15,
                                                    scale: 1.3,
                                                    animation: 'none',
                                                    effect: 'none',
                                                    zIndex: 50
                                                });
                                            }
                                            if (!newElements.some(el => el.id === 'section-subtitle')) {
                                                newElements.push({
                                                    id: 'section-subtitle',
                                                    name: '✨ Subtítulo Principal',
                                                    type: 'subtitle-text',
                                                    x: 50,
                                                    y: 22,
                                                    scale: 1.0,
                                                    animation: 'none',
                                                    effect: 'none',
                                                    zIndex: 49
                                                });
                                            }
                                            setElements(newElements);
                                        } else {
                                            setElements(elements.filter(el => el.id !== 'section-title' && el.id !== 'section-subtitle'));
                                        }
                                    }} 
                                />
                                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <button 
                                type="button"
                                onClick={() => handleAddElement('image')}
                                className="bg-indigo-50 text-indigo-600 border border-indigo-100 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all"
                            >
                                + Imagen Libre
                            </button>
                            <button 
                                type="button"
                                onClick={() => handleAddElement('text')}
                                className="bg-purple-50 text-purple-600 border border-purple-100 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-purple-100 transition-all"
                            >
                                + Texto Libre
                            </button>
                        </div>
                    </div>

                    {page === 'gallery' && (
                        <div className="space-y-4 pt-4 border-t border-slate-100">
                            <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2">Tipo de Galería Principal</p>
                            <div className="grid grid-cols-3 gap-2">
                                {['upload', 'embed', 'redirect'].map(type => (
                                    <button 
                                        key={type}
                                        onClick={() => onUpdate('type', type)}
                                        className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-widest border ${settings.type === type ? 'bg-slate-900 text-white border-slate-900 shadow-md' : 'bg-white text-slate-400 border-slate-100'}`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                            {(settings.type === 'embed' || settings.type === 'redirect') && (
                                <input 
                                    className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-mono shadow-sm" 
                                    placeholder="URL de Destino" 
                                    value={settings.url || ''} 
                                    onChange={e => onUpdate('url', e.target.value)} 
                                />
                            )}
                        </div>
                    )}

                    {page === 'agenda' && (
                        <div className="pt-4 border-t border-slate-100">
                            <button 
                                onClick={() => onUpdate('agendaImageOnly', !settings.agendaImageOnly)}
                                className={`flex items-center justify-between w-full px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all mb-4 ${settings.agendaImageOnly ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-white border border-slate-100 text-slate-300'}`}
                            >
                                <span>🖼️ Solo Imagen</span>
                                <span>{settings.agendaImageOnly ? 'SÍ' : 'NO'}</span>
                            </button>

                            {settings.agendaImageOnly && (
                                <div className="space-y-4 mb-6 p-5 bg-purple-50 rounded-3xl border border-purple-100 animate-fadeIn">
                                    <div>
                                        <label className="block text-[8px] font-black text-purple-600 uppercase tracking-widest mb-1 ml-1">URL Imagen Agenda Desktop (PC)</label>
                                        <input 
                                            className="w-full bg-white border border-purple-200 p-3 rounded-xl text-xs shadow-sm font-mono text-slate-800"
                                            placeholder="https://ejemplo.com/agenda_pc.png"
                                            value={settings.agendaImageDesktop || ''}
                                            onChange={e => onUpdate('agendaImageDesktop', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[8px] font-black text-purple-600 uppercase tracking-widest mb-1 ml-1">URL Imagen Agenda Móvil (Celular)</label>
                                        <input 
                                            className="w-full bg-white border border-purple-200 p-3 rounded-xl text-xs shadow-sm font-mono text-slate-800"
                                            placeholder="https://ejemplo.com/agenda_mobile.png"
                                            value={settings.agendaImageMobile || ''}
                                            onChange={e => onUpdate('agendaImageMobile', e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}


                            <div className="space-y-4 mb-4">
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Color/Gradiente Pestaña Activa (Por Defecto)</label>
                                    <input 
                                        className="w-full bg-white border border-slate-100 p-3 rounded-xl text-xs shadow-sm font-mono"
                                        placeholder="ej: bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]"
                                        value={settings.tabActiveGradient || 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]'}
                                        onChange={e => onUpdate('tabActiveGradient', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Color Texto Pestaña Activa (Por Defecto)</label>
                                    <div className="flex gap-2">
                                        <input type="color" className="w-10 h-10 rounded-lg cursor-pointer border-none" value={settings.tabTextColor || '#ffffff'} onChange={e => onUpdate('tabTextColor', e.target.value)} />
                                        <input className="flex-grow bg-white border border-slate-100 p-3 rounded-xl text-xs shadow-sm font-mono" value={settings.tabTextColor || '#ffffff'} onChange={e => onUpdate('tabTextColor', e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Orden de Subgrupos (Separados por coma)</label>
                                    <input 
                                        className="w-full bg-white border border-slate-100 p-3 rounded-xl text-xs shadow-sm font-mono"
                                        placeholder="ej: TEPEZZA, UPLIZNA, BKEMV"
                                        value={settings.subgroupOrder || ''}
                                        onChange={e => onUpdate('subgroupOrder', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-4 mb-4 p-5 bg-purple-50 rounded-3xl border border-purple-100 animate-fadeIn">
                                <p className="text-[8px] font-black text-purple-500 uppercase tracking-widest mb-2 ml-1">Gradientes Personalizados por Grupo</p>
                                {(settings.groupColors || []).map((gc, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-2xl border border-purple-200 relative group mb-2">
                                        <button 
                                            onClick={() => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc.splice(idx, 1);
                                                onUpdate('groupColors', newGc);
                                            }}
                                            className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs"
                                        >✕</button>
                                        
                                        {availableGroups.length > 0 ? (
                                            <select
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold cursor-pointer"
                                                value={gc.groupName || ''}
                                                onChange={e => {
                                                    const newGc = [...(settings.groupColors || [])];
                                                    newGc[idx].groupName = e.target.value;
                                                    onUpdate('groupColors', newGc);
                                                }}
                                            >
                                                <option value="" disabled>Seleccione un grupo de la BD...</option>
                                                {availableGroups.map(group => (
                                                    <option key={group} value={group}>{group}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold" 
                                                placeholder="Nombre del Grupo (ej: SHUA - HPN)" 
                                                value={gc.groupName} 
                                                onChange={e => {
                                                    const newGc = [...(settings.groupColors || [])];
                                                    newGc[idx].groupName = e.target.value;
                                                    onUpdate('groupColors', newGc);
                                                }} 
                                            />
                                        )}

                                        {availableSubgroups.length > 0 ? (
                                            <select
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold cursor-pointer"
                                                value={gc.subgroupName || ''}
                                                onChange={e => {
                                                    const newGc = [...(settings.groupColors || [])];
                                                    newGc[idx].subgroupName = e.target.value;
                                                    onUpdate('groupColors', newGc);
                                                }}
                                            >
                                                <option value="">Cualquier Subgrupo (Global)</option>
                                                {availableSubgroups.map(sub => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold" 
                                                placeholder="Nombre del Subgrupo (Opcional)" 
                                                value={gc.subgroupName || ''} 
                                                onChange={e => {
                                                    const newGc = [...(settings.groupColors || [])];
                                                    newGc[idx].subgroupName = e.target.value;
                                                    onUpdate('groupColors', newGc);
                                                }} 
                                            />
                                        )}
                                        
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono mb-2"  
                                            placeholder="Gradiente: bg-[linear-gradient(...)]" 
                                            value={gc.gradient} 
                                            onChange={e => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc[idx].gradient = e.target.value;
                                                onUpdate('groupColors', newGc);
                                            }} 
                                        />
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono mb-2" 
                                            placeholder="Color de Texto: #ffffff" 
                                            value={gc.textColor} 
                                            onChange={e => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc[idx].textColor = e.target.value;
                                                onUpdate('groupColors', newGc);
                                            }} 
                                        />
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                            placeholder="URL Descarga (PDF/Imagen)" 
                                            value={gc.downloadUrl || ''} 
                                            onChange={e => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc[idx].downloadUrl = e.target.value;
                                                onUpdate('groupColors', newGc);
                                            }} 
                                        />
                                    </div>
                                ))}
                                <button 
                                    onClick={() => {
                                        const current = settings.groupColors || [];
                                        onUpdate('groupColors', [...current, { groupName: 'Nuevo Grupo', gradient: 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]', textColor: '#ffffff' }]);
                                    }}
                                    className="w-full bg-white text-purple-600 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-purple-200 hover:bg-purple-100 transition-colors"
                                >
                                    + Agregar Gradiente a Grupo
                                </button>
                            </div>

                            <div className="space-y-4 mb-4 p-5 bg-blue-50 rounded-3xl border border-blue-100 animate-fadeIn">
                                <p className="text-[8px] font-black text-blue-500 uppercase tracking-widest mb-2 ml-1">Textos Legales / Footer por Grupo y Subgrupo</p>
                                {(settings.groupLegalTexts || []).map((glt, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-2xl border border-blue-200 relative group mb-2 space-y-3">
                                        <button 
                                            onClick={() => {
                                                const newGlt = [...(settings.groupLegalTexts || [])];
                                                newGlt.splice(idx, 1);
                                                onUpdate('groupLegalTexts', newGlt);
                                            }}
                                            className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs"
                                        >✕</button>
                                        
                                        <div className="grid grid-cols-2 gap-2 pt-2">
                                            <select
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-bold cursor-pointer"
                                                value={glt.groupName || ''}
                                                onChange={e => {
                                                    const newGlt = [...(settings.groupLegalTexts || [])];
                                                    newGlt[idx].groupName = e.target.value;
                                                    onUpdate('groupLegalTexts', newGlt);
                                                }}
                                            >
                                                <option value="">Cualquier Grupo (Global)</option>
                                                {availableGroups.map(group => (
                                                    <option key={group} value={group}>{group}</option>
                                                ))}
                                            </select>
                                            <select
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-bold cursor-pointer"
                                                value={glt.subgroupName || ''}
                                                onChange={e => {
                                                    const newGlt = [...(settings.groupLegalTexts || [])];
                                                    newGlt[idx].subgroupName = e.target.value;
                                                    onUpdate('groupLegalTexts', newGlt);
                                                }}
                                            >
                                                <option value="">Cualquier Subgrupo</option>
                                                {availableSubgroups.map(sub => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="flex items-center justify-between">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mostrar Legal / Footer</label>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input type="checkbox" className="sr-only peer" checked={glt.showLegalText || false} onChange={e => {
                                                    const newGlt = [...(settings.groupLegalTexts || [])];
                                                    newGlt[idx].showLegalText = e.target.checked;
                                                    onUpdate('groupLegalTexts', newGlt);
                                                }} />
                                                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                                            </label>
                                        </div>

                                        {glt.showLegalText && (
                                            <input 
                                                className="w-full bg-white border border-slate-100 p-3 rounded-xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                                                placeholder="URL del texto legal (ej. https://.../legal.pdf)"
                                                value={glt.legalTextUrl || ''}
                                                onChange={e => {
                                                    const newGlt = [...(settings.groupLegalTexts || [])];
                                                    newGlt[idx].legalTextUrl = e.target.value;
                                                    onUpdate('groupLegalTexts', newGlt);
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                                <button 
                                    onClick={() => {
                                        const newGlt = [...(settings.groupLegalTexts || []), { groupName: '', subgroupName: '', showLegalText: false, legalTextUrl: '' }];
                                        onUpdate('groupLegalTexts', newGlt);
                                    }}
                                    className="w-full bg-white text-blue-600 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-blue-200 hover:bg-blue-100 transition-colors"
                                >
                                    + Agregar Configuración de Legal
                                </button>
                            </div>                            {settings.agendaImageOnly && (
                                <div className="p-5 bg-orange-50 rounded-3xl border border-orange-100 animate-fadeIn space-y-4">
                                    <p className="text-[8px] font-black text-orange-400 uppercase tracking-widest mb-2 ml-1">Imágenes de Agenda por Grupo</p>
                                    
                                    {(settings.imageAgendas || []).map((ag, idx) => (
                                        <div key={idx} className="bg-white p-4 rounded-2xl border border-orange-200 relative group">
                                            <button 
                                                onClick={() => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs.splice(idx, 1);
                                                    onUpdate('imageAgendas', newAgs);
                                                }}
                                                className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs"
                                            >✕</button>
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold" 
                                                placeholder="Nombre del Grupo (ej: Día 1)" 
                                                value={ag.group_name || ''} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].group_name = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold" 
                                                placeholder="Nombre del Subgrupo (Opcional)" 
                                                value={ag.subgroup_name || ''} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].subgroup_name = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono mb-2" 
                                                placeholder="URL de la Imagen" 
                                                value={ag.staticImage} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].staticImage = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                                placeholder="URL Descarga (PDF/Imagen)" 
                                                value={ag.downloadUrl || ''} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].downloadUrl = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                        </div>
                                    ))}

                                    <button 
                                        onClick={() => {
                                            const current = settings.imageAgendas || [];
                                            onUpdate('imageAgendas', [...current, { group_name: 'Nuevo Grupo', staticImage: '' }]);
                                        }}
                                        className="w-full bg-white text-orange-500 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-orange-200 hover:bg-orange-100 transition-colors"
                                    >
                                        + Agregar Grupo
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {page === 'home' && (
                        <div className="pt-4 border-t border-slate-100">
                            <p className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Videos Configurables</p>
                            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                                {(settings.videos || []).map((video, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative group">
                                        <button 
                                            onClick={() => {
                                                const newVids = [...(settings.videos || [])];
                                                newVids.splice(idx, 1);
                                                onUpdate('videos', newVids);
                                            }}
                                            className="absolute top-2 right-2 text-slate-300 hover:text-red-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                        >✕</button>
                                        
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="checkbox" 
                                                    checked={video.active} 
                                                    onChange={e => {
                                                        const newVids = [...(settings.videos || [])];
                                                        newVids[idx].active = e.target.checked;
                                                        onUpdate('videos', newVids);
                                                    }} 
                                                    className="w-4 h-4 accent-purple-600"
                                                />
                                                <input 
                                                    className="flex-grow bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs font-bold" 
                                                    placeholder="Título del Video" 
                                                    value={video.title} 
                                                    onChange={e => {
                                                        const newVids = [...(settings.videos || [])];
                                                        newVids[idx].title = e.target.value;
                                                        onUpdate('videos', newVids);
                                                    }} 
                                                />
                                            </div>
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                                placeholder="URL de YouTube Embed o MP4" 
                                                value={video.videoUrl} 
                                                onChange={e => {
                                                    const newVids = [...(settings.videos || [])];
                                                    newVids[idx].videoUrl = e.target.value;
                                                    onUpdate('videos', newVids);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                                placeholder="URL de Miniatura (Opcional)" 
                                                value={video.thumbnailUrl} 
                                                onChange={e => {
                                                    const newVids = [...(settings.videos || [])];
                                                    newVids[idx].thumbnailUrl = e.target.value;
                                                    onUpdate('videos', newVids);
                                                }} 
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button 
                                onClick={() => {
                                    const current = settings.videos || [];
                                    onUpdate('videos', [...current, { title: 'Nuevo Video', videoUrl: '', thumbnailUrl: '', active: true }]);
                                }}
                                className="mt-4 w-full bg-slate-100 text-slate-500 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-colors"
                            >
                                + Añadir Video
                            </button>
                        </div>
                    )}

                    <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
                        {elements.map(el => (
                            <div 
                                key={el.id}
                                onClick={() => setSelectedElementId(el.id)}
                                className={`p-3 rounded-xl cursor-pointer border flex justify-between items-center transition-all ${selectedElementId === el.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg' : 'bg-white border-slate-100 text-slate-600 hover:border-indigo-200'}`}
                            >
                                <span className="text-xs font-bold truncate">{el.name}</span>
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleRemoveElement(el.id); }}
                                    className={`text-xs ${selectedElementId === el.id ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-red-500'}`}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Editor & Iframe */}
                <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-8">
                    
                    {/* Visual Canvas Editor */}
                    <div className="space-y-4">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Editor Visual</label>
                        <div 
                            className="relative bg-slate-200 rounded-[30px] border-4 border-white shadow-xl overflow-hidden aspect-video select-none"
                            ref={canvasRef}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            {/* Background */}
                            {settings.bgImage && (
                                <img src={settings.bgImage} alt="Background" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                            )}
                            
                            {/* Elements */}
                            {elements.map(el => (
                                <div
                                    key={el.id}
                                    onMouseDown={(e) => handleMouseDown(e, el.id)}
                                    className={`absolute cursor-move transition-transform ${selectedElementId === el.id ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}`}
                                    style={{
                                        left: `${el.x}%`,
                                        top: `${el.y}%`,
                                        transform: `translate(-50%, -50%) scale(${el.scale || 1})`,
                                        zIndex: el.zIndex || 10,
                                        filter: getEffectStyle(el.effect)
                                    }}
                                >
                                    {el.type === 'title-text' ? (
                                        <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-wider drop-shadow-lg whitespace-nowrap select-none">
                                            {settings.title || 'TÍTULO SECCIÓN'}
                                        </h2>
                                    ) : el.type === 'subtitle-text' ? (
                                        settings.subtitleType === 'image' && settings.subtitleImage ? (
                                            <img src={settings.subtitleImage} alt="Subtitle logo" className="max-w-[120px] h-auto object-contain pointer-events-none" />
                                        ) : (
                                            <div className="inline-block px-3 py-0.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20 select-none">
                                                <p className="text-[9px] font-bold text-white uppercase tracking-widest whitespace-nowrap">
                                                    {settings.subtitle || 'Subtítulo Sección'}
                                                </p>
                                            </div>
                                        )
                                    ) : el.type === 'text' ? (
                                        <div 
                                            style={{ 
                                                color: el.color || '#ffffff', 
                                                fontSize: el.fontSize || '16px',
                                                fontFamily: el.fontFamily || 'inherit',
                                                fontWeight: el.fontWeight || 'bold'
                                            }}
                                            className="drop-shadow-md text-center max-w-[200px] break-words select-none"
                                        >
                                            {el.text || 'Texto libre'}
                                        </div>
                                    ) : el.url ? (
                                        <img src={el.url} alt={el.name} className="max-w-[120px] max-h-[120px] object-contain pointer-events-none" />
                                    ) : (
                                        <div className="w-20 h-20 bg-slate-300/80 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-400 text-[10px] font-bold text-slate-600 p-2 text-center pointer-events-none backdrop-blur-sm">
                                            Sin Imagen
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Selected Element Properties */}
                        {selectedElement && (
                            <div className="bg-white p-6 rounded-[20px] shadow-sm border border-slate-100 animate-fadeIn space-y-4">
                                <h4 className="text-xs font-black uppercase tracking-widest text-slate-800 border-b pb-2">Propiedades: {selectedElement.name}</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Nombre</label>
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs"
                                            value={selectedElement.name}
                                            onChange={(e) => updateElement(selectedElement.id, 'name', e.target.value)}
                                        />
                                    </div>
                                    {(selectedElement.type === 'text' || selectedElement.type === 'title-text' || selectedElement.type === 'subtitle-text') ? (
                                        <>
                                            {selectedElement.type === 'text' && (
                                                <div className="col-span-2">
                                                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Texto del Elemento</label>
                                                    <textarea 
                                                        className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs focus:outline-none min-h-[60px]"
                                                        value={selectedElement.text || ''}
                                                        onChange={(e) => updateElement(selectedElement.id, 'text', e.target.value)}
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Color de Texto</label>
                                                <div className="flex gap-2">
                                                    <input 
                                                        type="color" 
                                                        className="w-8 h-8 rounded-lg cursor-pointer border-none p-0 bg-transparent" 
                                                        value={selectedElement.color || '#ffffff'} 
                                                        onChange={(e) => updateElement(selectedElement.id, 'color', e.target.value)} 
                                                    />
                                                    <input 
                                                        className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs font-mono uppercase" 
                                                        value={selectedElement.color || '#ffffff'} 
                                                        onChange={(e) => updateElement(selectedElement.id, 'color', e.target.value)} 
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Tipografía</label>
                                                <select 
                                                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs appearance-none"
                                                    value={selectedElement.fontFamily || 'Alliance-Light'}
                                                    onChange={(e) => updateElement(selectedElement.id, 'fontFamily', e.target.value)}
                                                >
                                                    <option value="Alliance-Light">Alliance Light</option>
                                                    <option value="Alliance-SemiBold">Alliance SemiBold</option>
                                                    <option value="Drina">Drina (Script)</option>
                                                    <option value="sans-serif">Sans Serif</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Tamaño Texto</label>
                                                <input 
                                                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs"
                                                    placeholder="24px o 1.5rem"
                                                    value={selectedElement.fontSize || '24px'}
                                                    onChange={(e) => updateElement(selectedElement.id, 'fontSize', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Grosor de Texto</label>
                                                <select 
                                                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs appearance-none"
                                                    value={selectedElement.fontWeight || 'bold'}
                                                    onChange={(e) => updateElement(selectedElement.id, 'fontWeight', e.target.value)}
                                                >
                                                    <option value="normal">Normal</option>
                                                    <option value="bold">Bold (Negrita)</option>
                                                    <option value="black">Black (Gruesa)</option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="col-span-2">
                                            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">URL Imagen</label>
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs font-mono"
                                                placeholder="URL PNG/SVG"
                                                value={selectedElement.url || ''}
                                                onChange={(e) => updateElement(selectedElement.id, 'url', e.target.value)}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Escala ({selectedElement.scale || 1}x)</label>
                                        <input 
                                            type="range"
                                            min="0.1" max="3" step="0.1"
                                            className="w-full accent-indigo-600"
                                            value={selectedElement.scale || 1}
                                            onChange={(e) => updateElement(selectedElement.id, 'scale', parseFloat(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Efecto</label>
                                        <select 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs appearance-none"
                                            value={selectedElement.effect || 'none'}
                                            onChange={(e) => updateElement(selectedElement.id, 'effect', e.target.value)}
                                        >
                                            <option value="none">Sin Efecto</option>
                                            <option value="shadow">Sombreado Básico</option>
                                            <option value="neon">Neón Difusión</option>
                                            <option value="glow">Glow Blanco</option>
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Animación de Entrada</label>
                                        <select 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs appearance-none"
                                            value={selectedElement.animation || 'none'}
                                            onChange={(e) => updateElement(selectedElement.id, 'animation', e.target.value)}
                                        >
                                            <option value="none">Sin Animación</option>
                                            <option value="fade-in">Fade In</option>
                                            <option value="slide-left">Slide Izquierda</option>
                                            <option value="slide-right">Slide Derecha</option>
                                            <option value="bounce">Bounce</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Iframe Preview */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Previsualización (Iframe)</label>
                            <span className="text-[8px] bg-emerald-100 text-emerald-600 px-2 py-1 rounded-md font-bold uppercase tracking-widest animate-pulse">Live</span>
                        </div>
                        <div className="relative bg-slate-100 rounded-[30px] border-4 border-slate-800 shadow-2xl overflow-hidden aspect-[9/16] md:aspect-[3/4] mx-auto max-w-[350px]">
                            {/* Device Notch Simulation */}
                            <div className="absolute top-0 inset-x-0 h-6 bg-slate-800 rounded-b-2xl w-1/2 mx-auto z-50"></div>
                            <iframe 
                                src={`${clientUrl}/${page === 'home' ? '' : page}`} 
                                title={`Preview ${page}`}
                                className="w-full h-full bg-white border-none"
                            />
                        </div>
                        <p className="text-center text-[9px] text-slate-400 uppercase font-bold tracking-widest">Vista Móvil Aproximada</p>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SectionDesignEditor;
